import { useState, useEffect, useRef } from 'react';

/**
 * Hook to fetch lecture clips from Supabase.
 *
 * MULTI-TENANT SAFETY:
 * - centerId is passed as a STABLE PRIMITIVE (string), not derived internally.
 * - The hook NEVER resolves center_id from domain — that's the caller's job.
 * - Effect depends on centerId (string), not config (object), so it only
 *   re-fetches when the actual center identity changes, not on every
 *   config object reference change.
 * - Stale fetch guard: if centerId changes mid-flight, the old response
 *   is silently discarded.
 *
 * @param {string|null} currentSeries - Series key to filter by, or 'all'
 * @param {string|null} centerId - Center UUID. If null/undefined, hook idles.
 * @returns {{ clips, series, loading, error, refetch }}
 */
const useClipsData = (currentSeries, centerId) => {
  const [clips, setClips] = useState([]);
  const [allClips, setAllClips] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchGenRef = useRef(0); // Generation counter for stale fetch detection

  // Fetch clips — re-runs ONLY when centerId (string) changes.
  // Not when config object reference changes. This eliminates the race condition.
  useEffect(() => {
    if (!centerId) {
      setLoading(false);
      return;
    }

    const generation = ++fetchGenRef.current;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { supabase } = await import('../../../lib/supabase');

        const { data, error: queryError } = await supabase
          .from('lecture_clips')
          .select('*')
          .eq('center_id', centerId)
          .eq('is_visible', true)
          .order('published_at', { ascending: false });

        // Discard if a newer fetch was started (centerId changed mid-flight)
        if (cancelled || generation !== fetchGenRef.current) return;

        if (queryError) throw queryError;

        if (!data || data.length === 0) {
          setAllClips([]);
          setClips([]);
          setSeries([]);
          setLoading(false);
          return;
        }

        const transformed = data.map(row => ({
          id: row.id,
          driveFileId: row.drive_file_id,
          title: row.title || 'Untitled Clip',
          series: row.series || 'Uncategorized',
          eventName: row.series || 'Uncategorized',
          speakerName: row.speaker || 'Speaker',
          lectureId: row.lecture_id,
          language: row.language || 'English',
          clipType: row.clip_type || 'shorts',
          orderIndex: row.order_index || 0,
          publishedAt: row.published_at,
          likeCount: row.like_count || 0,
          viewCount: row.view_count || 0,
          playCount: row.play_count || 0,
          isVisible: row.is_visible,
        }));

        const seriesSet = new Map();
        transformed.forEach(clip => {
          if (!seriesSet.has(clip.series)) {
            seriesSet.set(clip.series, clip.publishedAt);
          }
        });
        const uniqueSeries = Array.from(seriesSet.entries())
          .sort((a, b) => new Date(b[1]) - new Date(a[1]))
          .map(([name]) => name);

        setAllClips(transformed);
        setSeries(uniqueSeries);
        setLoading(false);
      } catch (err) {
        if (cancelled || generation !== fetchGenRef.current) return;
        console.error('[useClipsData] Error fetching clips:', err);
        setError(err.message || 'Failed to load lecture clips');
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [centerId]); // ONLY depends on centerId string — stable, no race

  // Filter clips by series (client-side)
  useEffect(() => {
    if (!allClips.length) {
      setClips([]);
      return;
    }
    if (!currentSeries || currentSeries === 'all') {
      setClips(allClips);
    } else {
      setClips(allClips.filter(c => c.series === currentSeries));
    }
  }, [allClips, currentSeries]);

  // Refetch — bumps generation to re-trigger the effect
  const refetch = () => {
    fetchGenRef.current++;
    // Force re-run by toggling a dummy state
    setError(null);
    setLoading(true);
  };

  return { clips, series, loading, error, refetch };
};

export default useClipsData;
